
import * as React from "react";
import {
    Button,
    Input,
    Popover,
    PopoverTrigger,
    PopoverSurface,
} from "@fluentui/react-components";
import {
    ChevronDown12Regular,
} from "@fluentui/react-icons";
import { SocialIcon } from 'react-social-icons';
import { apiUrl } from "../utils/api";

type Communication = {
    id: number;
    type: 'phone' | 'fax' | 'email' | 'mobile' | 'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'xing' | 'youtube' | 'homepage';
    label: string;
    value: string;
    isDefault: boolean;
};

type SocialMediaIconProps = {
    platform: Communication['type'];
    existing?: Communication;
    contactId: number;
    onSave: (comm: Communication) => void;
};

export const SocialMediaIcon: React.FC<SocialMediaIconProps> = ({ platform, existing, contactId, onSave }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [currentValue, setCurrentValue] = React.useState(existing?.value || "");
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        setCurrentValue(existing?.value || "");
    }, [existing]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const commData = {
                type: platform,
                label: platform.charAt(0).toUpperCase() + platform.slice(1),
                value: currentValue,
                isDefault: false,
            };

            const url = existing
                ? apiUrl(`contact/${contactId}/communication/${existing.id}`)
                : apiUrl(`contact/${contactId}/communication`);

            const method = existing ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(commData),
            });

            if (response.ok) {
                const newComm = await response.json();
                onSave(newComm);
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Error saving social media link:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const iconColor = existing?.value ? undefined : "#A19F9D"; // Brand color or grey

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "2px", marginTop: "10px" }}>
            <div
                onClick={() => existing?.value && window.open(existing.value, '_blank')}
                style={{ cursor: existing?.value ? "pointer" : "default", display: "flex" }}
                title={existing?.value ? `Besuche ${platform}` : "Keine URL hinterlegt"}
            >
                <SocialIcon
                    network={platform === 'homepage' ? undefined : platform}
                    url={platform === 'homepage' ? currentValue : undefined}
                    style={{ height: 24, width: 24 }}
                    bgColor={iconColor}
                    borderRadius="4px"
                />
            </div>
            <Popover open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
                <PopoverTrigger disableButtonEnhancement>
                    <div style={{ display: "flex", alignItems: "center", cursor: "pointer", padding: "4px 2px" }}>
                        <ChevronDown12Regular style={{ color: "#605E5C" }} />
                    </div>
                </PopoverTrigger>
                <PopoverSurface style={{ padding: "12px", width: "300px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Input
                            value={currentValue}
                            onChange={(_, data) => setCurrentValue(data.value)}
                            placeholder="https://..."
                            style={{ width: "100%" }}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <Button size="small" onClick={() => setIsOpen(false)}>Abbrechen</Button>
                            <Button
                                size="small"
                                appearance="primary"
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? "Lädt..." : "Speichern"}
                            </Button>
                        </div>
                    </div>
                </PopoverSurface>
            </Popover>
        </div>
    );
};
